/**
 * Générateur — Nommer un calcul, distinguer sommes et produits, termes et facteurs.
 * Attendu ID : 696 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 3)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « rectangle_bleu » — Ex.3 (figure SVG OBLIGATOIRE)
 *     Format : qcm_multiple (les DEUX expressions qui calculent l'aire du
 *     rectangle bleu, compte annoncé). Le classer par nature a été RETIRÉ de
 *     cette carte (retour responsable : hors sujet sur la carte géométrique) —
 *     il vit dans « Somme ou produit ? ». Figure à l'échelle : grand rectangle
 *     L × c, carré c × c grisé à gauche, rectangle bleu à droite. Bonnes
 *     expressions PAR GÉOMÉTRIE : (L − c) × c et L × c − c × c.
 *
 *   Carte « salaire » — Ex.6 Le menuisier (pont O5/O6)
 *     Formats : qcm_multiple (les DEUX expressions égales, avec et sans
 *     parenthèses), texte_trou_saisie (calcul par la forme simple :
 *     « 160 × {{10,01}} = {{1601,6}} € »). Le QCM « laquelle est un
 *     produit ? » a été retiré (retour responsable : la reconnaissance de
 *     nature est déjà couverte par « Somme ou produit ? ») ; le relier du doc
 *     avait déjà été écarté (deux cibles identiques violeraient la bijection 🔴).
 *
 *   Carte « somme_ou_produit » — Créé n°2 (enrichie, retour responsable)
 *     Formats : classer, tableau_vf, qcm_multiple (compte annoncé).
 *     Groupes construits avec les MÊMES nombres ; DEUX MODES en rotation :
 *       - 2 catégories : 3 paires somme (a + b × c) / produit ((a + b) × c)
 *       - 3 catégories : 2 triplets somme / produit / différence (a × b − c)
 *     Catégorie = opérateur de tête (structurel, unique).
 *
 *   Carte « vocabulaire » — Créé n°3
 *     Formats : texte_trou_drag (étiquettes : somme, produit, termes,
 *     facteurs — les mots non attendus servent de distracteurs naturels),
 *     relier (expression ↔ une somme / un produit / une différence / un
 *     quotient — 4 cibles DISTINCTES, mêmes nombres partout).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 696;

const L_RECT = "Trouver l'aire du rectangle bleu (figure)";
const L_SAL  = 'Deux expressions pour un salaire';
const L_SOP  = 'Somme ou produit ?';
const L_VOC  = 'Le vocabulaire du calcul';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Centimes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — L'aire du rectangle bleu (figure)
// ═══════════════════════════════════════════════════════════

/**
 * L, c en dixièmes ; L > 2c pour un rectangle bleu bien visible.
 * 🔴 Contrainte anti-coïncidence (bug attrapé par le solveur) : le piège
 * « L + c » ne doit JAMAIS valoir l'aire (L − c) × c — sinon la question a
 * deux expressions numériquement bonnes (ex. 6,3 + 1,8 = 8,1 = 4,5 × 1,8).
 */
const tirerRectangleBleu = () => {
    for (let i = 0; i < 40; i++) {
        const c10 = randInt(15, 35);
        const L10 = randInt(2 * c10 + 15, 2 * c10 + 90);
        // valeurs en centièmes : (L + c) × 10 vs (L − c) × c
        if ((L10 + c10) * 10 === (L10 - c10) * c10) continue;
        return { L: dixToFr(L10), c: dixToFr(c10), L10, c10 };
    }
    return { L: '12', c: '2,4', L10: 120, c10: 24 };   // l'exercice source (12 + 2,4 ≠ 23,04)
};

const svgRectangleBleu = ({ L, c, L10, c10 }) => {
    const s = 30;                                  // px par cm
    const w = L10 * s / 10, h = c10 * s / 10;
    const wc = h;                                  // carré c × c
    const pad = 64;
    const W = Math.round(w + 2 * pad), H = Math.round(h + 76);
    const x0 = pad, y0 = 44;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        `<rect x="${x0}" y="${y0}" width="${wc}" height="${h}" fill="#CBD5E1" stroke="#1E3A8A" stroke-width="2"/>` +
        `<rect x="${x0 + wc}" y="${y0}" width="${w - wc}" height="${h}" fill="#60A5FA" stroke="#1E3A8A" stroke-width="2"/>` +
        `<text x="${x0 + w / 2}" y="${y0 - 10}" text-anchor="middle" font-size="14" fill="#0f172a">${L} cm</text>` +
        `<line x1="${x0}" y1="${y0 - 26}" x2="${x0 + w}" y2="${y0 - 26}" stroke="#64748b" stroke-width="1"/>` +
        `<text x="${x0 - 10}" y="${y0 + h / 2 + 5}" text-anchor="end" font-size="14" fill="#0f172a">${c} cm</text>` +
        `<text x="${x0 + wc / 2}" y="${y0 + h / 2 + 5}" text-anchor="middle" font-size="13" fill="#334155">carré</text>` +
        `<text x="${x0 + wc + (w - wc) / 2}" y="${y0 + h / 2 + 5}" text-anchor="middle" font-size="13" fill="#ffffff" font-weight="bold">?</text>` +
        '</svg>';
};

const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

/** Les 6 expressions du doc, paramétrées — natures par opérateur de tête. */
const expressionsRectangle = ({ L, c }) => [
    { txt: `${L} + ${c}`,               aire: false, nature: 'Somme' },
    { txt: `${L} × ${c}`,               aire: false, nature: 'Produit' },
    { txt: `${L} − ${c} × ${c}`,        aire: false, nature: 'Différence' },
    { txt: `(${L} − ${c}) × ${c}`,      aire: true,  nature: 'Produit' },
    { txt: `${L} × ${c} − ${c} × ${c}`, aire: true,  nature: 'Différence' },
    { txt: `${L} × ${c} − ${c} × ${L}`, aire: false, nature: 'Différence' },
];

const enonceRectangle = ({ L, c }) =>
    '<p>La figure est formée d\'un carré (gris) et d\'un rectangle bleu. ' +
    `La longueur totale est <strong>${L} cm</strong> et le côté du carré mesure ` +
    `<strong>${c} cm</strong>.</p>`;

const genRectangleQcmMulti = () => {
    const t = tirerRectangleBleu();
    const exprs = shuffle(expressionsRectangle(t));
    const corrects = exprs.filter(e => e.aire).map(e => e.txt);
    const texte = enonceRectangle(t) +
        '<p>Coche les <strong>deux</strong> expressions qui calculent l\'aire du rectangle bleu.</p>';
    const payload = makeQcm(exprs.map(e => e.txt), corrects, texte, opts(L_RECT), true);
    payload.elements_additionnels = imageElement(svgRectangleBleu(t));
    return payload;
};

// (Le classer par nature a été retiré de cette carte — retour du responsable
// pédagogique : il n'a pas de sens sur la carte géométrique. La classification
// par nature vit dans la carte « Somme ou produit ? », enrichie de la
// catégorie Différence.)

// ═══════════════════════════════════════════════════════════
// Carte 2 — Deux expressions pour un salaire (menuisier)
// ═══════════════════════════════════════════════════════════

const CTX_MENUISIER = [
    'Un menuisier', 'Une électricienne', 'Un plombier', 'Une carreleuse',
];

const tirerMenuisier = () => {
    const h = randomChoice([140, 148, 152, 160]);
    const s = randInt(13, 17);
    const rC = randomChoice([199, 249, 299]);
    const netC = s * 100 - rC;
    return { qui: randomChoice(CTX_MENUISIER), h, s, rF: centToFr(rC), netC, totalC: h * netC };
};

const enonceMenuisier = ({ qui, h, s, rF }) =>
    `<p>${qui} travaille <strong>${h} heures par mois</strong> à <strong>${s} €</strong> ` +
    `de l'heure. On déduit <strong>${rF} €</strong> de cotisations sociales par heure.</p>`;

const genMenuisierQcmMulti = () => {
    const t = tirerMenuisier();
    const { h, s, rF } = t;
    const bonnes = [`${h} × (${s} − ${rF})`, `${h} × ${s} − ${h} × ${rF}`];
    const pieges = [
        `${h} × ${s} − ${rF}`,
        `(${h} − ${rF}) × ${s}`,
        `${h} × ${s} + ${h} × ${rF}`,
    ];
    const texte = enonceMenuisier(t) +
        '<p>Coche les <strong>deux</strong> expressions qui calculent le salaire mensuel net ' +
        '(l\'une avec parenthèses, l\'autre sans).</p>';
    return makeQcm(shuffle([...bonnes, ...pieges]), bonnes, texte, opts(L_SAL), true);
};

// (Le QCM « laquelle est un produit ? » a été retiré — retour du responsable :
// la reconnaissance de nature est déjà couverte par « Somme ou produit ? ».)

const genMenuisierTrous = () => {
    const t = tirerMenuisier();
    const { h, s, rF } = t;
    const phrase = `${h} × (${s} − ${rF}) = ${h} × {{${centToFr(t.netC)}}} = {{${centToFr(t.totalC)}}} €`;
    const texte = enonceMenuisier(t) +
        '<p>Calcule le salaire mensuel net par la méthode la plus simple. ' +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_SAL), mode: 'saisie' });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Somme ou produit ?
// ═══════════════════════════════════════════════════════════

/**
 * Groupes d'expressions aux MÊMES nombres, natures différentes (opérateur de
 * tête). Deux modes en rotation (enrichissement demandé par le responsable) :
 *   - 2 catégories : 3 paires  somme (a + b × c)  /  produit ((a + b) × c)
 *   - 3 catégories : 2 triplets somme / produit / différence (a × b − c)
 */
const tirerExpressionsNature = () => {
    const troisCat = randomChoice([true, false]);
    const nbGroupes = troisCat ? 2 : 3;
    const groupes = [];
    const vus = new Set();
    for (let i = 0; groupes.length < nbGroupes && i < 40; i++) {
        const a = randomChoice([2, 3, 4, 5, 7, 8, '2,5', '1,5']);
        const b = randInt(2, 9);
        const cc = randInt(2, 9);
        const cle = `${a}|${b}|${cc}`;
        if (vus.has(cle) || b === cc) continue;
        // La différence a × b − c doit rester positive (on ne la calcule pas,
        // mais un résultat mentalement négatif troublerait un élève de 5e)
        if (troisCat && parseFloat(String(a).replace(',', '.')) * b <= cc) continue;
        vus.add(cle);
        const groupe = [
            { txt: `${a} + ${b} × ${cc}`, nature: 'Somme' },        // tête : +
            { txt: `(${a} + ${b}) × ${cc}`, nature: 'Produit' },    // tête : ×
        ];
        if (troisCat) groupe.push({ txt: `${a} × ${b} − ${cc}`, nature: 'Différence' });  // tête : −
        groupes.push(groupe);
    }
    while (groupes.length < nbGroupes) {
        const g = [
            { txt: '7 + 5 × 3', nature: 'Somme' },
            { txt: '(7 + 5) × 3', nature: 'Produit' },
        ];
        if (troisCat) g.push({ txt: '7 × 5 − 3', nature: 'Différence' });
        groupes.push(g);
    }
    return { troisCat, exprs: groupes.flat() };
};

const genSommeOuProduitClasser = () => {
    const { troisCat, exprs } = tirerExpressionsNature();
    const natures = troisCat ? ['Somme', 'Différence', 'Produit'] : ['Somme', 'Produit'];
    const categories = natures.map(nature => ({
        name: nature,
        items: exprs.filter(e => e.nature === nature).map(e => e.txt),
    }));
    const texte = '<p>Classe chaque expression dans la bonne colonne. ' +
        'Attention : les nombres se ressemblent — regarde la <strong>dernière</strong> ' +
        'opération effectuée !</p>';
    return makeClasser(categories, texte, opts(L_SOP));
};

const genSommeOuProduitVF = () => {
    const { exprs } = tirerExpressionsNature();
    // 2 produits (V) + 2 non-produits (F : sommes ou différences) → mix garanti
    const produits = shuffle(exprs.filter(e => e.nature === 'Produit')).slice(0, 2);
    const autres = shuffle(exprs.filter(e => e.nature !== 'Produit')).slice(0, 2);
    const lignes = shuffle([
        ...produits.map(e => ({ txt: `${e.txt} est un produit`, v: true })),
        ...autres.map(e => ({ txt: `${e.txt} est un produit`, v: false })),
    ]);
    const texte = '<p>Pour chaque expression, indique si l\'affirmation est vraie ou fausse.</p>';
    return makeVraiFaux(lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_SOP));
};

const MOTS_COMPTE = { 2: 'deux', 3: 'trois' };

const genSommeOuProduitQcmMulti = () => {
    const { exprs } = tirerExpressionsNature();
    const corrects = exprs.filter(e => e.nature === 'Produit').map(e => e.txt);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[corrects.length]}</strong> expressions ` +
        'qui sont des <strong>produits</strong>.</p>';
    return makeQcm(shuffle(exprs.map(e => e.txt)), corrects, texte, opts(L_SOP), true);
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Le vocabulaire du calcul
// ═══════════════════════════════════════════════════════════

const MOTS_VOCAB = ['somme', 'produit', 'termes', 'facteurs'];

const genVocabulaireTrouDrag = () => {
    const a = dixToFr(randInt(21, 199));
    const b = randInt(3, 9);
    const cc = randInt(2, 9);
    const variante = randInt(0, 2);
    let phrase;
    if (variante === 0) {
        phrase = `${a} + ${b} × ${cc} est une {{somme}} dont les {{termes}} sont ${a} et ${b} × ${cc}.`;
    } else if (variante === 1) {
        phrase = `(${a} + ${b}) × ${cc} est un {{produit}} dont les {{facteurs}} sont (${a} + ${b}) et ${cc}.`;
    } else {
        phrase = `Dans le produit ${cc} × (${a} + ${b}), les {{facteurs}} sont ${cc} et (${a} + ${b}).`;
    }
    const distracteurs = filtrerDistracteurs(phrase, MOTS_VOCAB);
    const texte = '<p>Complète avec le bon vocabulaire :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_VOC), mode: 'drag', distracteurs });
};

const genVocabulaireRelier = () => {
    const a = randInt(3, 15);
    const b = randInt(2, 9);
    const pairs = shuffle([
        { source: `${a} + ${b}`, target: 'une somme' },
        { source: `${a} − ${b}`, target: 'une différence' },
        { source: `${a} × ${b}`, target: 'un produit' },
        { source: `${a} ÷ ${b}`, target: 'un quotient' },
    ]);
    const texte = '<p>Relie chaque expression à sa nature. ' +
        'Les nombres sont les mêmes partout : seule l\'opération change !</p>';
    return makeRelier(pairs, texte, opts(L_VOC));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    rectangle_bleu_qcm_multiple:   genRectangleQcmMulti,
    salaire_expressions_qcm_multiple: genMenuisierQcmMulti,
    salaire_calcul_texte_trou_saisie: genMenuisierTrous,
    somme_ou_produit_classer:      genSommeOuProduitClasser,
    somme_ou_produit_tableau_vf:   genSommeOuProduitVF,
    somme_ou_produit_qcm_multiple: genSommeOuProduitQcmMulti,
    vocabulaire_texte_trou_drag:   genVocabulaireTrouDrag,
    vocabulaire_relier:            genVocabulaireRelier,
};

export const CATEGORIES = [
    {
        id: 'rectangle_bleu', label: L_RECT,
        formatsList: [
            { key: 'rectangle_bleu_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'salaire', label: L_SAL,
        formatsList: [
            { key: 'salaire_expressions_qcm_multiple', label: 'QCM Multi' },
            { key: 'salaire_calcul_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'somme_ou_produit', label: L_SOP,
        formatsList: [
            { key: 'somme_ou_produit_classer',      label: 'Classer' },
            { key: 'somme_ou_produit_tableau_vf',   label: 'Vrai/Faux' },
            { key: 'somme_ou_produit_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'vocabulaire', label: L_VOC,
        formatsList: [
            { key: 'vocabulaire_texte_trou_drag', label: 'Drag' },
            { key: 'vocabulaire_relier',          label: 'Relier' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    dixToFr, centToFr,
    tirerRectangleBleu, expressionsRectangle, svgRectangleBleu,
    tirerMenuisier, tirerExpressionsNature,
};
